import EditorFacade from '../facade/editor/editor';
import { uploadAsset, addAssetToLibrary } from 'src/utils/upload-image';

let Tool;
let uniqueVideos = [];

export function init () {
  Tool = INKIVE.Tool.getInstance();
  initDragAndDropEvents.call(this);
  initUppyComponent.call(this);

  EditorFacade.on('library.root', ({ type, content }) => {
    if (type === 'videos') uniqueVideos = content;
  });
}

function initDragAndDropEvents () {
  const $container = $('#svgcanvas');

  $container.on('dragover', (event) => {
    event.stopPropagation();
    event.preventDefault();

    event = event.originalEvent;
    event.dataTransfer.dropEffect = 'copy';
  });

  $container.on('drop', (event) => {
    event.stopPropagation();
    event.preventDefault();

    const files = event.originalEvent.dataTransfer.files;

    const images = [...files].filter(imageTypeFilter);
    const icons = [...files].filter(iconTypeFilter);
    const videos = [...files].filter(videoTypeFilter);

    images.forEach((file) => imageDropped(file));
    icons.forEach((file) => iconDropped(file));

    if (videos.length) {
      Tool.editor.canvas.call('advice.show', 'uploading-file');
      videos.forEach((file) => videoDropped.call(this, file));
    }

    if (!images.length && !videos.length && !icons.length) {
      Tool.editor.canvas.call('advice.show', 'file-not-allowed');
    }
  });

  function imageTypeFilter (file) {
    return ['image/png', 'image/jpeg'].includes(file.type);
  }

  function iconTypeFilter (file) {
    return file.type === 'image/svg+xml';
  }

  function videoTypeFilter (file) {
    // Checks for unique videos by name
    const duplicated = uniqueVideos.reduce((bool, videoJ) => (
      bool || file.name === videoJ.data.name
    ), false);

    return !duplicated && ['video/mp4', 'video/quicktime'].includes(file.type);
  }
}

export function imageDropped (file) {
  insertImageOnCanvas(file)
    .then(async (image) => {
      const assetType = 'image';

      const { url } = await uploadAsset(file, file.name, file.type, assetType);

      image.classList.remove('drag-and-drop-upload');

      INKIVE.DesignFacade.set(image.id, {
        skipHistory: true,
        source: {
          url,
          preload: true,
          external_resize: false
        },
        keep_box_size: true
      });

      addAssetToLibrary(
        url,
        file.name,
        parseFloat(image.getAttribute('height')),
        parseFloat(image.getAttribute('width')),
        assetType
      );

      INKIVE.DesignFacade.trigger('image.dropped', { file });

      return url;
    })
    .catch(console.error);
}

export function iconDropped (file) {
  insertIconOnCanvas(file)
    .then(async (icon) => {
      const assetType = 'icon';

      const { url } = await uploadAsset(file, file.name, file.type, assetType);

      icon.classList.remove('drag-and-drop-upload');

      INKIVE.DesignFacade.set(icon.id, {
        skipHistory: true,
        source: {
          url,
          preload: true,
          external_resize: false
        },
        keep_box_size: true
      });

      addAssetToLibrary(
        url,
        file.name,
        parseFloat(icon.getAttribute('height')),
        parseFloat(icon.getAttribute('width')),
        assetType
      );

      INKIVE.DesignFacade.trigger('image.dropped', { file });

      return url;
    })
    .catch(console.error);
}

function videoDropped (file) {
  document.querySelector('.btn-tag[href="#tabVideo"]').click();

  setTimeout(() => {
    INKIVE.SVGElementLoading.show(document.body);
    document.getElementById('tabVideo').querySelector(`[data-key=${this.libraryName}]`).click();

    this.uppyFactory
      ? uploadVideo(this.uppyFactory.uppy)
      : EditorFacade.once('video-uppy-loaded', uploadVideo);
  }, 100);

  function uploadVideo (uppy) {
    uppy.addFile({
      name: file.name,
      type: file.type,
      data: file
    });

    uppy.upload().then((result) => {
      INKIVE.SVGElementLoading.hide(document.body);
      if (!result || result.failed.length || !result.successful.length) {
        Tool.editor.canvas.call('advice.show', 'file-not-allowed');
      }
    });
  }
}

function insertImageOnCanvas (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = onReaderData;
    reader.readAsDataURL(file);

    function onReaderData (event) {
      const image = new Image();
      image.src = event.target.result;
      image.onload = onImageLoad;
      image.onerror = reject;
    }

    function onImageLoad (event) {
      const img = event.currentTarget;

      const canvasSize = {
        width: INKIVE.EditorFacade.canvas.contentW,
        height: INKIVE.EditorFacade.canvas.contentH
      };

      const { width, height } = calculateRelativeAssetSize(
        canvasSize,
        event.currentTarget
      );

      const x = (canvasSize.width - width) / 2;
      const y = (canvasSize.height - height) / 2;

      const id = INKIVE.DesignFacade.create('image', {
        imported: false,
        source: {
          url: img.src
        },
        position: {
          x,
          y
        },
        size: {
          width,
          height
        }
      });

      const image = document.getElementById(id);

      image.classList.add('drag-and-drop-upload');

      Tool.editor.canvas.call('loading.show', image);
      releaseSelector();
      resolve(image);
    }

    function releaseSelector () {
      const selectorManager = svgedit.select.getSelectorManager();
      selectorManager.releaseAllSelectors();
    }
  });
}

function insertIconOnCanvas (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = onReaderData;
    reader.readAsText(file);

    function onReaderData (event) {
      const svgContent = event.target.result;
      const image = new Image();
      image.src = `data:image/svg+xml;base64,${window.btoa(svgContent)}`;
      image.onload = (imageLoadEvent) => onImageLoad(imageLoadEvent, svgContent);
      image.onerror = reject;
    }

    function onImageLoad (event, svgContent) {
      const canvasSize = {
        width: INKIVE.EditorFacade.canvas.contentW,
        height: INKIVE.EditorFacade.canvas.contentH
      };

      const { width, height } = calculateRelativeAssetSize(
        canvasSize,
        event.currentTarget
      );

      const x = (canvasSize.width - width) / 2;
      const y = (canvasSize.height - height) / 2;

      const id = INKIVE.DesignFacade.create('sticker', {
        selected: true,
        position: {
          x,
          y
        },
        size: {
          width,
          height
        },
        content: svgContent
      });

      const image = document.getElementById(id);

      image.classList.add('drag-and-drop-upload');

      Tool.editor.canvas.call('loading.show', image);
      releaseSelector();
      resolve(image);
    }

    function releaseSelector () {
      const selectorManager = svgedit.select.getSelectorManager();
      selectorManager.releaseAllSelectors();
    }
  });
}

function calculateRelativeAssetSize (canvasSize, assetSize) {
  const SVGRatio = canvasSize.width / canvasSize.height;
  const imageRatio = assetSize.width / assetSize.height;
  const sizeLimit = 0.7;

  const width =
    imageRatio > SVGRatio
      ? canvasSize.width * sizeLimit
      : canvasSize.height * sizeLimit * imageRatio;

  const height =
    imageRatio > SVGRatio ? width / imageRatio : canvasSize.height * sizeLimit;

  return {
    width,
    height
  };
}

function initUppyComponent () {
  this.libraryName = INKIVE.Helpers.projectIsLinkedToRestrictions()
    ? 'user_library'
    : 'library';
  EditorFacade.on(
    `videos-${this.libraryName}-upload-button-appended`,
    (response) => {
      this.uppyFactory = response;
      EditorFacade.trigger('video-uppy-loaded', this.uppyFactory.uppy);
    }
  );
}
